<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div class="m-2">
      <b-row class="d-flex align-items-center">
        <b-col
          md="4"
          class="mb-2"
        >
          <validation-observer ref="batchGlCustomerPostingRules">
            <b-form>
              <b-col
                md="12"
              >
                <div class="mb-1">
                  {{ $t('Select file (.xlsx format only)') }}
                </div>
                <b-form-file
                  id="file-name"
                  v-model="hasFileData"
                  :label="$t('Select file (.xlsx format only)')"
                  :state="Boolean(hasFileData)"
                  :placeholder="$t('Choose a file or drop it here...')"
                  :drop-placeholder="$t('Drop file here...')"
                  error-name="file"
                  rules="required"
                  accept=".xlsx"
                  @change="onChangeXLSX($event)"
                />
              </b-col>
            </b-form>
          </validation-observer>
        </b-col>

        <b-col
          md="8"
        >
          <b-row>
            <b-col
              md="2"
            >
              <Button
                v-if="getUserPermission('BATCH_JOURNAL_UPLOAD_POSTING') && parseExcelFile.length"
                class="mt-1"
                :label="$t('Next >')"
                size="md"
                :hidden="isNextValidateHidden"
                :loading="isLoading"
                @click="handleNextValidate"
              />
              <Button
                v-if="getUserPermission('BATCH_JOURNAL_UPLOAD_POSTING') && !errorMessages.length"
                class="mt-1"
                :label="$t('Submit Posting')"
                size="md"
                :hidden="isCreateBatchHidden"
                :loading="isLoading"
                :disabled="isDisabled"
                @click="handleCreate"
              />
            </b-col>
            <b-col
              v-if="getUserPermission('DEBIT_DORMANT_ACCOUNT')"
              md="2"
              class="mt-2"
            >
              <SwitchButton
                v-model="allowDebitOnDormantAccounts"
                :label="$t('Allow Debit on Dormant Accounts')"
                name="allow-debit-on-dormant-accounts"
              />
            </b-col>
            <b-col
              md="2"
              class="mt-2"
            >
              <SwitchButton
                v-model="forceDebit"
                :label="$t('Allow Force Debit?')"
                name="force-debit"
              />
            </b-col>
            <b-col
              md="2"
              class="mt-2"
            >
              <SwitchButton
                v-model="enablePreview"
                :label="$t('Enable Preview?')"
                name="enable-preview"
              />
            </b-col>
            <b-col
              md="1"
              class="mt-2"
            >
              <SwitchButton
                v-model="ignoreCOTCharge"
                :label="$t('CAM FEE')"
                name="cot-fee"
              />
            </b-col>
            <b-col
              md="2"
            >
              <Button
                class="ml-lg-2 mt-1"
                align="end"
                :label="$t('Download Batch Posting Format')"
                size="md"
                icon="DownloadIcon"
                show-icon
                :loading="isLoading2"
                :disabled="isDisabled2"
                @click="handleDownloadSubmit"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <div v-if="parseExcelFile.length">
      <b-row class="mb-2">
        <b-col md="5" />
        <b-col md="7">
          <b-row style="font-weight: 700;">
            <b-col> {{ $t('Total Debit Amount:') }} <span style="font-size: 18px;">{{ totalDebit ? toAmount(totalDebit) : `&#8358;0.00` }}</span></b-col>
            <b-col> {{ $t('Total Credit Amount:') }} <span style="font-size: 18px;">{{ totalCredit ? toAmount(totalCredit) : `&#8358;0.00` }}</span></b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <b-table
      v-if="errorMessages.length"
      ref="batchGlPostingSuccessReport"
      :items="errorMessages"
      responsive
      :fields="translatedErrorHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    />
    <p
      v-if="totalSheetRows && !enablePreview"
      class="p-2"
      style="font-size: 18px;"
    >
      {{ $t('There are total of') }} ({{ totalSheetRows }}) {{ $t('Records fetched') }}
    </p>
    <b-table
      v-if="enablePreview"
      ref="glCustomerPostingRef"
      :items="batchCustomerPostingList"
      responsive
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(debit)="data">
        <p class="mb-0">
          {{ data.value ? toBalance(data.value) : '' }}
        </p>
      </template>
      <template #cell(credit)="data">
        <p class="mb-0">
          {{ data.value ? toBalance(data.value) : '' }}
        </p>
      </template>
      <template #cell(availableBalance)="data">
        <p class="mb-0">
          {{ data.value ? toBalance(data.value) : '' }}
        </p>
      </template>
    </b-table>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BForm, BTable, BFormFile,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions, mapGetters } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import XLSX from 'xlsx'
import { toAmount, toKobo, toBalance } from '@/helpers/formatAmount'
import SwitchButton from '../../components/switch/Switch.vue'
import Button from '../../components/button/Button.vue'
import { batchAccountFormat, tableLessHeaders, tableAllHeaders } from '../../../helpers/data'
import { postingPermissions } from '@/helpers/permissions'

export default {
  components: {
    BCard,
    BTable,
    BRow,
    BCol,
    BForm,
    BFormFile,
    ValidationObserver,
    SwitchButton,
    Button,
  },
  filters: {
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      totalSheetRows: '',
      enablePreview: true,
      toAmount,
      toBalance,
      fileData: [],
      hasFileData: [],
      parseExcelFile: [],
      batchCustomerPostingList: [],
      forceDebit: false,
      ignoreCOTCharge: false,
      allowDebitOnDormantAccounts: false,
      validatedExcelFileData: [],
      isCreateBatchHidden: true,
      isNextValidateHidden: false,
      sortOrders: {},
      accountFormat: batchAccountFormat,
      successHeaders: tableAllHeaders,
      failedHeaders: tableLessHeaders,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number(GL or Customer)' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'branchCode', sortable: true, label: 'Branch Code' },
        { key: 'branchName', sortable: true, label: 'Branch Name' },
        { key: 'availableBalance', sortable: true, label: 'Available Balance' },
        { key: 'debit', sortable: true, label: 'Debit' },
        { key: 'credit', sortable: true, label: 'Credit' },
        { key: 'instrumentNumber', sortable: true, label: 'Instrument Number' },
        { key: 'entryCode', sortable: true, label: 'Sort Code' },
        { key: 'narration', sortable: true, label: 'Transaction Details or Narration' },
        { key: 'accountType', sortable: true, label: 'Account Type' },
        { key: 'casaNumberSource', sortable: true, label: 'CASA Number Source' },
      ],
      required,
      isDisabled: true,
      isLoading: false,
      isDisabled2: false,
      isLoading2: false,
      showFormError: false,
      sortDesc: false,
      postingType: 'BatchJournalToCustomerPosting',
      sortBy: 'lastName',
      errorHeaders: [
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'message', sortable: true, label: 'Error Messages' },
      ],
      errorMessages: [],
    }
  },

  computed: {
    totalDebit() {
      return this.parseExcelFile.reduce((acc, item) => acc + Number(item.debit), 0)
    },
    totalCredit() {
      return this.parseExcelFile.reduce((acc, item) => acc + Number(item.credit), 0)
    },
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedErrorHeaders() {
      return this.errorHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  methods: {
    ...mapActions({
      validateBatchGlPostingCreation: 'posting/validateBatchGlPostingCreation',
      batchGlPostingCreation: 'posting/postingBatchPostingAction',
      downloadAccountFormat: 'batch/downloadAccountFormat',
    }),
    async handleNextValidate() {
      try {
        this.isLoading = true
        this.hasFileData = []
        const formData = this.parseExcelFile.map(item => ({
          ...item,
          casaNumberSource: item.casaNumberSource ? Number(item.casaNumberSource) : 0,
          debit: item.debit ? Number(toKobo(item.debit).toFixed()) : 0,
          credit: item.credit ? Number(toKobo(item.credit).toFixed()) : 0,
        }))

        const postingApprovalModel = {
          amount: Number(toKobo(this.totalCredit)),
          initiatedBy: '',
          approvedBy: '',
          postingType: this.postingType,
        }
        const permission = postingPermissions.VIEW_BATCH_GL_CUSTOMER_UPLOAD_POSTING
        const response = await this.validateBatchGlPostingCreation({
          payload: formData, forceDebit: this.forceDebit, ignoreCOTCharge: this.ignoreCOTCharge, allowDebitOnDormantAccounts: this.allowDebitOnDormantAccounts, initiatedBy: this.userDetails.email, postingApprovalModel, permission,
        })
        const { status, data } = response
        if (status === 200 && data) {
          this.batchCustomerPostingList = data.payload
          this.isNextValidateHidden = true
          this.alertSuccess({ text: this.$t('Everything seems good! Continue with Batch GL and Customer posting upload') })
          this.isCreateBatchHidden = false
          this.isDisabled = false
          this.isLoading = false
        }
      } catch (error) {
        this.handleError(error)
        this.isNextValidateHidden = false
        this.isCreateBatchHidden = true
        this.isDisabled = true
        this.isLoading = false
      } finally {
        this.isLoading = false
      }
    },
    async handleCreate() {
      this.$refs.batchGlCustomerPostingRules.validate().then(success => {
        if (success) {
          this.confirm(this.batchGlPostingCreationAction, { text: this.$t('Are you sure you want to upload a batch posting?') })
        }
      })
    },
    async batchGlPostingCreationAction() {
      try {
        this.showFormError = false
        this.isDisabled = true
        this.isLoading = true
        const formData = this.batchCustomerPostingList.map(item => ({
          ...item,
        }))
        const postingApprovalModel = {
          amount: Number(toKobo(this.totalCredit)),
          initiatedBy: '',
          approvedBy: '',
          postingType: this.postingType,
        }
        const permission = postingPermissions.BATCH_GL_CUSTOMER_POSTING
        const response = await this.batchGlPostingCreation({
          payload: formData, forceDebit: this.forceDebit, ignoreCOTCharge: this.ignoreCOTCharge, allowDebitOnDormantAccounts: this.allowDebitOnDormantAccounts, initiatedBy: this.userDetails.email, postingApprovalModel, permission,
        })
        const { status, data } = response
        if (status === 200 && data.isSuccessful) {
          this.hasFileData = []
          this.parseExcelFile = []
          this.batchCustomerPostingList = []
          if (data.referenceNumber) {
            const successText = this.$t('Your Batch upload request was successful. Batch upload reference ID is')
            this.alertSuccess({ text: `${successText} ${data.referenceNumber}` })
          } else {
            this.alertSuccess({ text: data.message })
          }
        } else if (status === 200 && !data.isSuccessful) {
          const errorText = this.$t('Your Batch upload request failed. Reason')
          this.alertError({ text: `${errorText}: ${data.message}` })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const text = this.$t('Your Batch upload request failed. Reason')
          this.alertError({ text: `${text}: ${responseError.errorMessage}` })
        })
        this.showFormError = true
      } finally {
        this.isDisabled = false
        this.isLoading = false
      }
    },
    onChangeXLSX(event) {
      this.isDisabled = false
      this.fileData = event.target.files ? event.target.files[0] : null
      if (this.fileData) {
        const vm = this
        const reader = new FileReader()
        reader.onload = e => {
          const data = e.target.result
          const workbook = XLSX.read(data, { type: 'binary' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const rowObject = XLSX.utils.sheet_to_row_object_array(worksheet, { raw: false })
          vm.totalSheetRows = rowObject.length
          vm.parseExcelFile = JSON.parse(JSON.stringify(rowObject)).map(items => ({
            accountNumber: items['Account Number(GL or Customer)'],
            branchCode: items['Branch Code'],
            debit: items['Debit Amount'] === undefined ? 0 : items['Debit Amount'],
            credit: items['Credit Amount'] === undefined ? 0 : items['Credit Amount'],
            instrumentNumber: items['Instrument Number'],
            entryCode: items['Sort Code'],
            narration: items['Transaction Details or Narration'],
            accountType: items['Account Number Type'],
            casaNumberSource: items['CASA Number Source'] === undefined ? 0 : items['CASA Number Source'],
          }))
          this.handleNextValidate()
        }
        reader.readAsBinaryString(this.fileData)
      }
    },
    async handleDownloadSubmit() {
      this.confirm(this.downloadAction, { text: this.$t('Are you sure you want to download Batch Posting Format?') })
    },
    async downloadAction() {
      try {
        this.isLoading2 = true
        this.isDisabled2 = true
        const BatchAccountFileTemplate = 16
        const response = await this.downloadAccountFormat(BatchAccountFileTemplate)
        if (response.status === 200) {
          const downloadAnchor = document.createElement('a')
          downloadAnchor.setAttribute('href', response.data.data)
          const filename = 'Udara360-GL-Customer-Upload-Posting.xlsx'
          downloadAnchor.setAttribute('download', filename)
          document.body.appendChild(downloadAnchor)
          downloadAnchor.click()
          document.body.removeChild(downloadAnchor)
          this.alertSuccess({ text: this.$t('Your request to download Batch Posting Format was successful') })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const reasonText = this.$t('Your request to download Batch Posting Format failed. Reason')
          this.alertError({ text: `${reasonText}: ${responseError.errorMessage}` })
          this.isLoading2 = false
          this.isDisabled2 = false
        })
      } finally {
        this.isLoading2 = false
        this.isDisabled2 = false
      }
    },
  },
}
</script>
